import {
	Box,
	Code,
	Fieldset,
	Grid,
	NumberInput,
	Space,
	Tabs,
	TextInput,
	Tooltip,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
	IconBarcode,
	IconBasketDollar,
	IconBlocks,
	IconBuildingSkyscraper,
	IconCalculator,
	IconCategory,
	IconClipboardText,
	IconLock,
	IconPentagonNumber1,
	IconReceiptTax,
	IconRollercoaster,
	IconShoppingCart,
	IconTextCaption,
	IconTruckDelivery,
	IconUsersGroup,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { FormGenericSelector } from '../../../components/form/FormGenericSelector';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { UpsertPriceForm } from './UpsertPriceForm';

type UpsertProductFormProps = {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	form: UseFormReturnType<any, (values: any) => any>;
	isEdit?: boolean;
};

function TabErrorDot() {
	return <Box w={8} h={8} bg={'red.6'} style={{ borderRadius: '50%' }} />;
}

export function UpsertProductForm({
	form,
	isEdit = false,
}: UpsertProductFormProps) {
	const { organisation } = useParams();

	const hasPricesError = Object.keys(form.errors).some((field) =>
		field.startsWith('prices.'),
	);

	const hasProductError = Object.keys(form.errors).some(
		(field) => !field.startsWith('prices.'),
	);

	const skuidInputProps = {
		label: 'SKUID',
		placeholder: 'Insert SKUID...',
		disabled: isEdit,
		key: form.key('skuid'),
		...form.getInputProps('skuid'),
	};

	return (
		<Tabs defaultValue={'product'} color={'medepos.1'}>
			<Tabs.List>
				<Tabs.Tab
					value={'product'}
					rightSection={hasProductError && <TabErrorDot />}
				>
					Product
				</Tabs.Tab>
				<Tabs.Tab
					value={'prices'}
					rightSection={hasPricesError && <TabErrorDot />}
				>
					Prices
				</Tabs.Tab>
			</Tabs.List>
			<Tabs.Panel value={'product'} pt={10}>
			<Fieldset legend={'Information'}>
				<Grid>
					<Grid.Col span={8}>
						<TextInput
							label={'Description'}
							placeholder={'Insert description of new product...'}
							leftSection={<IconTextCaption {...defaultIconStyle} />}
							key={form.key('description')}
							{...form.getInputProps('description')}
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						{isEdit ? (
							<Tooltip label={'Cannot edit SKUID'}>
								<TextInput
									{...skuidInputProps}
									leftSection={<IconLock {...defaultIconStyle} />}
								/>
							</Tooltip>
						) : (
							<TextInput
								{...skuidInputProps}
								leftSection={<IconClipboardText {...defaultIconStyle} />}
							/>
						)}
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							label={'Brand'}
							placeholder={'Insert brand...'}
							leftSection={<IconRollercoaster {...defaultIconStyle} />}
							key={form.key('brand')}
							{...form.getInputProps('brand')}
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							label={'Model'}
							placeholder={'Insert model...'}
							leftSection={<IconBlocks {...defaultIconStyle} />}
							key={form.key('model')}
							{...form.getInputProps('model')}
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							label={'Master Barcode'}
							placeholder={'Insert master barcode...'}
							leftSection={<IconBarcode {...defaultIconStyle} />}
							key={form.key('barcode')}
							{...form.getInputProps('barcode')}
						/>
					</Grid.Col>
					<Grid.Col span={3}>
						<FormGenericSelector
							label={'Group'}
							placeholder={'Select group...'}
							icon={<IconUsersGroup {...defaultIconStyle} />}
							form={form}
							property={'groupId'}
							apiRoute={`${organisation}/groups/options`}
						/>
					</Grid.Col>
					<Grid.Col span={3}>
						<FormGenericSelector
							label={'Department'}
							placeholder={'Select department...'}
							icon={<IconBuildingSkyscraper {...defaultIconStyle} />}
							form={form}
							property={'departmentId'}
							apiRoute={`${organisation}/departments/options`}
						/>
					</Grid.Col>
					<Grid.Col span={3}>
						<FormGenericSelector
							label={'Supplier'}
							placeholder={'Select supplier...'}
							icon={<IconShoppingCart {...defaultIconStyle} />}
							form={form}
							property={'supplierId'}
							apiRoute={`${organisation}/supplier/options`}
						/>
					</Grid.Col>
					<Grid.Col span={3}>
						<FormGenericSelector
							label={'VAT Rate'}
							placeholder={'Select VAT rate...'}
							icon={<IconReceiptTax {...defaultIconStyle} />}
							form={form}
							property={'vatRateId'}
							apiRoute={`${organisation}/vat-rates/options`}
						/>
					</Grid.Col>
				</Grid>
			</Fieldset>
			<Space h={20} />
			<Fieldset legend={'Stock & Cost'}>
				<Grid>
					<Grid.Col span={4}>
						<NumberInput
							label={'Case Cost'}
							placeholder={'Insert case cost...'}
							key={form.key('caseCost')}
							{...form.getInputProps('caseCost')}
							decimalScale={2}
							prefix="£"
							leftSection={<IconBasketDollar {...defaultIconStyle} />}
							allowNegative={false}
							thousandSeparator={','}
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<NumberInput
							label={'Case Quantity'}
							placeholder={'Insert number of items in case...'}
							key={form.key('caseQuantity')}
							{...form.getInputProps('caseQuantity')}
							allowDecimal={false}
							allowNegative={false}
							leftSection={<IconCategory {...defaultIconStyle} />}
							thousandSeparator={','}
						/>
					</Grid.Col>
					<Grid.Col span={4}>
						<NumberInput
							label={'Single Cost'}
							placeholder={'Insert cost of single...'}
							key={form.key('singleCost')}
							{...form.getInputProps('singleCost')}
							decimalScale={2}
							prefix="£"
							allowNegative={false}
							leftSection={<IconPentagonNumber1 {...defaultIconStyle} />}
							thousandSeparator={','}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<NumberInput
							label={'Stock Quantity'}
							placeholder={'Insert stock quantity...'}
							key={form.key('stockQuantity')}
							{...form.getInputProps('stockQuantity')}
							leftSection={<IconCalculator {...defaultIconStyle} />}
							allowDecimal={false}
							thousandSeparator={','}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<NumberInput
							label={'Order Quantity'}
							placeholder={'Insert order quantity...'}
							key={form.key('orderQuantity')}
							{...form.getInputProps('orderQuantity')}
							allowDecimal={false}
							allowNegative={false}
							leftSection={<IconTruckDelivery {...defaultIconStyle} />}
							thousandSeparator={','}
						/>
					</Grid.Col>
						</Grid>
					</Fieldset>
				</Tabs.Panel>
				<Tabs.Panel value={'prices'} pt={10}>
					<Code>
						{isEdit
							? 'Prices are only saved when changed. Saving a change will override the prices of every branch for this product.'
							: 'These prices will be applied to the branches created with this product.'}
					</Code>
					<Space h={10} />
					<UpsertPriceForm form={form} prefix={'prices.'} />
				</Tabs.Panel>
		</Tabs>
	);
}
